import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import api from "../service";

/**
 * Fonte única dos tipos de serviço para telas de seleção (dropdowns/forms).
 * Consome o catálogo do backend (`GET /provider-association/service-types`), cujo `name` já vem
 * localizado no idioma do usuário, e devolve a forma `[{ value: id, label: name, typeName }]` —
 * a mesma forma esperada pelas telas de seleção (dropdowns/forms).
 *
 * Invariants:
 * - A chave de cache é (`intl.locale` + `token`); `locale` participa apenas como identidade de
 *   cache/invalidação — a decisão pt/en é feita no backend (não há threading de idioma). Incluir o
 *   `token` invalida o cache ao trocar de usuário na mesma sessão SPA (o catálogo do usuário
 *   anterior não é servido ao novo), sem depender de limpeza no fluxo de logout.
 * - cache de módulo POR CHAVE; o resultado exposto é SEMPRE o da chave atual — nunca dados de outra
 *   chave. Ao trocar de idioma/usuário sem cache da nova chave, a lista é zerada enquanto carrega
 *   (evita exibir rótulos do estado anterior). Cache-hit da chave atual é instantâneo no remount.
 * - deduplicação da Promise em voo por chave (telas simultâneas não disparam requests duplicados).
 * - erro NÃO é cacheado: a lista fica vazia e a Promise em voo é limpa; uma nova tentativa ocorre em
 *   remount do componente, troca de `intl.locale` ou troca de `token`.
 *
 * Observação: a lista de opções é independente do valor selecionado no formulário (state do
 * consumidor); zerar as opções durante loading/erro NÃO apaga a seleção do usuário.
 */

const cacheByKey = new Map(); // cacheKey -> Array<{ value, label, typeName }>
const inflightByKey = new Map(); // cacheKey -> Promise<Array<...>>

const buildCacheKey = (locale, token) => `${locale}::${token || ""}`;

const mapCatalog = (output) =>
  (output || []).map((item) => ({
    value: item.id,
    label: item.name,
    typeName: item.typeName,
  }));

const fetchProvisionTypes = (cacheKey, token) => {
  if (inflightByKey.has(cacheKey)) {
    return inflightByKey.get(cacheKey);
  }

  const promise = api
    .get("/provider-association/service-types", {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((response) => {
      const list = mapCatalog(response.data.output);
      cacheByKey.set(cacheKey, list);
      inflightByKey.delete(cacheKey);
      return list;
    })
    .catch((err) => {
      // Não cacheia falha: remove a Promise em voo para permitir nova tentativa.
      inflightByKey.delete(cacheKey);
      throw err;
    });

  inflightByKey.set(cacheKey, promise);
  return promise;
};

const initialState = (cacheKey) => ({
  cacheKey,
  provisionTypes: cacheByKey.get(cacheKey) || [],
  loading: !cacheByKey.has(cacheKey),
  error: null,
});

const useProvisionTypes = () => {
  const intl = useIntl();
  const locale = intl.locale;
  const token = useSelector((state) => state.auth.token);
  const cacheKey = buildCacheKey(locale, token);

  const [state, setState] = useState(() => initialState(cacheKey));

  useEffect(() => {
    let active = true;

    const cached = cacheByKey.get(cacheKey);
    if (cached) {
      setState({ cacheKey, provisionTypes: cached, loading: false, error: null });
      return () => {
        active = false;
      };
    }

    // Sem cache da chave atual: zera a lista (nao herda dados de outra chave) e carrega.
    setState({ cacheKey, provisionTypes: [], loading: true, error: null });

    fetchProvisionTypes(cacheKey, token)
      .then((list) => {
        if (active) {
          setState({ cacheKey, provisionTypes: list, loading: false, error: null });
        }
      })
      .catch((err) => {
        if (active) {
          setState({ cacheKey, provisionTypes: [], loading: false, error: err });
        }
      });

    return () => {
      active = false;
    };
  }, [cacheKey, token]);

  // Estado visivel derivado da chave atual: no primeiro render apos a troca de idioma/usuario (antes
  // de o effect rodar) o `state` ainda e da chave anterior, entao nao o expomos. Cache-aware via
  // initialState (chave em cache -> imediato; sem cache -> [] + loading).
  const visibleState = state.cacheKey === cacheKey ? state : initialState(cacheKey);

  return {
    provisionTypes: visibleState.provisionTypes,
    loading: visibleState.loading,
    error: visibleState.error,
  };
};

export default useProvisionTypes;
